import vaultService from './vaultService';

/**
 * Memory Surfacing Service
 * Detects patterns for "Coming Back to You" cards:
 * 1. Forgotten Gem
 * 2. Last Month's Highlights  
 * 3. Focus Heating Up
 * 4. Group These Together
 * 
 * NOW USES VAULT SERVICE for unified data access.
 */

// Helper: Extract micro-topics from items
function extractMicroTopics(items) {
  const topics = [];
  items.forEach(item => {
    if (item.title) topics.push(item.title.toLowerCase());
    if (item.description) topics.push(item.description.toLowerCase());
    if (item.auto_tags && Array.isArray(item.auto_tags)) {
      topics.push(...item.auto_tags.map(tag => tag.toLowerCase()));
    }
  });
  return topics;
}

// Helper: Find common keywords between two sets of items
function findCommonKeywords(items1, items2, minCount = 2) {
  const topics1 = extractMicroTopics(items1);
  const topics2 = extractMicroTopics(items2);
  
  const wordCounts = {};
  
  // Count words from first set
  topics1.forEach(topic => {
    const words = topic.split(/\s+/).filter(w => w.length > 3);
    words.forEach(word => {
      if (!wordCounts[word]) wordCounts[word] = { count1: 0, count2: 0 };
      wordCounts[word].count1++;
    });
  });
  
  // Count words from second set
  topics2.forEach(topic => {
    const words = topic.split(/\s+/).filter(w => w.length > 3);
    words.forEach(word => {
      if (wordCounts[word]) wordCounts[word].count2++;
    });
  });
  
  // Find common keywords
  const commonKeywords = Object.entries(wordCounts)
    .filter(([word, counts]) => counts.count1 >= minCount && counts.count2 >= minCount)
    .sort((a, b) => (b[1].count1 + b[1].count2) - (a[1].count1 + a[1].count2))
    .slice(0, 5)
    .map(([word]) => word);
  
  return commonKeywords;
}

// Helper: Build micro-topic phrase from items
function buildMicroTopicPhrase(items, maxItems = 3) {
  const uniqueTopics = new Set();
  
  items.forEach(item => {
    if (item.title) {
      const cleaned = item.title.toLowerCase()
        .replace(/^(the|a|an)\s+/i, '')
        .trim();
      if (cleaned) uniqueTopics.add(cleaned);
    }
    if (item.auto_tags && Array.isArray(item.auto_tags)) {
      item.auto_tags.slice(0, 2).forEach(tag => uniqueTopics.add(tag.toLowerCase()));
    }
  });
  
  const topicsArray = Array.from(uniqueTopics).slice(0, maxItems);
  
  if (topicsArray.length === 0) return 'items';
  if (topicsArray.length === 1) return topicsArray[0];
  if (topicsArray.length === 2) return `${topicsArray[0]} and ${topicsArray[1]}`;
  
  const last = topicsArray[topicsArray.length - 1];
  const rest = topicsArray.slice(0, -1).join(', ');
  return `${rest}, and ${last}`;
}

// Helper: Calculate days ago
function daysAgo(date) {
  return Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * 1. FORGOTTEN GEM
 * Trigger: Item saved 21-90 days ago, not touched since, connects to current activity
 */
function detectForgottenGem(items) {
  if (!items || items.length < 5) {
    if (import.meta.env.DEV) console.log('[ForgottenGem] ❌ Not enough items:', items?.length || 0);
    return null;
  }
  
  const recent7Days = items.filter(item => daysAgo(item.created_at) <= 7);
  const forgotten = items.filter(item => {
    const days = daysAgo(item.created_at);
    return days >= 14 && days <= 90; // Lowered from 21 to 14 days
  });
  
  if (import.meta.env.DEV) console.log('[ForgottenGem] Recent (7d):', recent7Days.length, '| Forgotten (14-90d):', forgotten.length);
  
  if (recent7Days.length < 2 || forgotten.length === 0) {
    if (import.meta.env.DEV) console.log('[ForgottenGem] ❌ Thresholds not met');
    return null;
  }
  
  // Find forgotten items that relate to current activity
  const recentTopics = extractMicroTopics(recent7Days);
  const recentWords = new Set(
    recentTopics.flatMap(t => t.split(/\s+/).filter(w => w.length > 2))
  );
  
  if (import.meta.env.DEV) console.log('[ForgottenGem] Recent words:', Array.from(recentWords).slice(0, 10));
  
  const relevantForgotten = forgotten.filter(item => {
    const itemTopics = extractMicroTopics([item]);
    const itemWords = itemTopics.flatMap(t => t.split(/\s+/).filter(w => w.length > 2));
    return itemWords.some(word => recentWords.has(word));
  });
  
  if (import.meta.env.DEV) console.log('[ForgottenGem] Relevant forgotten items:', relevantForgotten.length);
  
  // If no exact matches, just pick the oldest forgotten item
  if (relevantForgotten.length === 0 && forgotten.length > 0) {
    if (import.meta.env.DEV) console.log('[ForgottenGem] No keyword match, using oldest forgotten item');
    const oldestForgotten = forgotten[forgotten.length - 1];
    const days = daysAgo(oldestForgotten.created_at);
    const itemName = oldestForgotten.title || 'an item';
    const recentMicroTopics = buildMicroTopicPhrase(recent7Days, 2);
    
    if (import.meta.env.DEV) console.log('[ForgottenGem] ✅ Detected (fallback)!');
    
    return {
      type: 'forgotten_gem',
      title: 'Forgotten Gem',
      subtitle: itemName,
      shortReason: `An item from ${days} days ago that might resurface.`,
      reason: `You saved "${itemName}" ${days} days ago and have not revisited it since. You have been actively saving ${recentMicroTopics} recently. This older piece might connect to your current direction in ways you did not see before. When items sit dormant for weeks and then your behavior shifts back toward related themes, it often signals that forgotten content is becoming relevant again.`,
      action: 'Revisit this and see if it still resonates.',
      itemId: oldestForgotten.id,
      metadata: {
        daysAgo: days,
        itemTitle: itemName,
        relatedItems: recent7Days.length
      }
    };
  }
  
  if (relevantForgotten.length === 0) {
    if (import.meta.env.DEV) console.log('[ForgottenGem] ❌ No relevant forgotten items');
    return null;
  }
  
  // Pick the most relevant forgotten item
  const selectedItem = relevantForgotten[0];
  const days = daysAgo(selectedItem.created_at);
  const itemName = selectedItem.title || 'this item';
  const recentMicroTopics = buildMicroTopicPhrase(recent7Days, 2);
  
  if (import.meta.env.DEV) console.log('[ForgottenGem] ✅ Detected!');
  
  return {
    type: 'forgotten_gem',
    title: 'Forgotten Gem',
    subtitle: itemName,
    shortReason: `From ${days} days ago — now connects to recent activity.`,
    reason: `You saved "${itemName}" ${days} days ago and have not touched it since. Now you are actively saving ${recentMicroTopics} again, and this forgotten piece shares keywords and themes with your current direction. When old saves resurface as relevant during new activity patterns, it usually means your thinking has evolved to connect ideas that were previously separate. This item bridges your past interest with your present focus.`,
    action: 'Revisit this and connect it to your current direction.',
    itemId: selectedItem.id,
    metadata: {
      daysAgo: days,
      itemTitle: itemName,
      relatedItems: recent7Days.length
    }
  };
}

/**
 * 2. LAST MONTH'S HIGHLIGHTS
 * Trigger: Items from last month match current week's micro-topics
 */
function detectLastMonthHighlights(items) {
  if (!items || items.length < 5) {
    if (import.meta.env.DEV) console.log('[LastMonthHighlights] ❌ Not enough items:', items?.length || 0);
    return null;
  }
  
  const thisWeek = items.filter(item => daysAgo(item.created_at) <= 14); // Extended to 14 days
  const lastMonth = items.filter(item => {
    const days = daysAgo(item.created_at);
    return days >= 21 && days <= 90; // Expanded range
  });
  
  if (import.meta.env.DEV) console.log('[LastMonthHighlights] This week (14d):', thisWeek.length, '| Last month (21-90d):', lastMonth.length);
  
  if (thisWeek.length < 2 || lastMonth.length < 2) {
    if (import.meta.env.DEV) console.log('[LastMonthHighlights] ❌ Thresholds not met');
    return null;
  }
  
  const commonKeywords = findCommonKeywords(thisWeek, lastMonth, 1); // Lowered from 2 to 1
  
  if (import.meta.env.DEV) console.log('[LastMonthHighlights] Common keywords:', commonKeywords);
  
  if (commonKeywords.length === 0) {
    if (import.meta.env.DEV) console.log('[LastMonthHighlights] ❌ No common keywords found');
    
    // Fallback: just show last month items if we have enough data
    if (lastMonth.length >= 3) {
      const microTopics = buildMicroTopicPhrase(lastMonth, 3);
      if (import.meta.env.DEV) console.log('[LastMonthHighlights] ✅ Detected (fallback)!');
      
      return {
        type: 'last_month_highlights',
        title: "Last Month's Highlights",
        subtitle: microTopics,
        shortReason: `These ${lastMonth.length} items from last month are resurfacing now.`,
        reason: `Last month you saved ${microTopics}—a cluster of ${lastMonth.length} related pieces. You have been active recently with ${thisWeek.length} new saves in the past two weeks. Even without direct keyword overlap, the timing and volume suggest your attention is circling back to similar territories. When last month's content resurfaces during current activity, it often indicates that themes are cycling back or that you are preparing to synthesize older ideas with newer context. These older saves might now fit into patterns you could not see before.`,
        action: 'Review this cluster to see if themes are re-emerging.',
        metadata: {
          lastMonthCount: lastMonth.length,
          thisWeekCount: thisWeek.length,
          commonKeywords: []
        }
      };
    }
    
    return null;
  }
  
  const matchingLastMonth = lastMonth.filter(item => {
    const itemText = `${item.title} ${item.description || ''}`.toLowerCase();
    return commonKeywords.some(keyword => itemText.includes(keyword));
  });
  
  if (import.meta.env.DEV) console.log('[LastMonthHighlights] Matching last month items:', matchingLastMonth.length);
  
  const microTopics = buildMicroTopicPhrase(matchingLastMonth.length > 0 ? matchingLastMonth : lastMonth, 3);
  const thisWeekCount = thisWeek.filter(item => {
    const itemText = `${item.title} ${item.description || ''}`.toLowerCase();
    return commonKeywords.some(keyword => itemText.includes(keyword));
  }).length;
  
  if (import.meta.env.DEV) console.log('[LastMonthHighlights] ✅ Detected!');
  
  return {
    type: 'last_month_highlights',
    title: "Last Month's Highlights",
    subtitle: microTopics,
      shortReason: `This ${commonKeywords[0]} theme is cycling back into focus.`,
    reason: `Last month you saved ${microTopics}—${matchingLastMonth.length || lastMonth.length} pieces clustered around ${commonKeywords.slice(0, 3).join(', ')}. This week you saved ${thisWeekCount} more items that share the same keywords and themes. This is not random: when themes from last month reappear in current saves, it signals that your curiosity is waking back up in a specific direction. You are revisiting ${commonKeywords[0]} with fresh context, which usually means you are ready to go deeper or synthesize what you collected before with what you are learning now.`,
    action: 'Review this cluster to strengthen the theme.',
    metadata: {
      lastMonthCount: matchingLastMonth.length || lastMonth.length,
      thisWeekCount,
      commonKeywords
    }
  };
}

/**
 * 3. FOCUS HEATING UP
 * Trigger: Dormant theme resurfaces with recent saves
 */
function detectFocusHeatingUp(items) {
  if (!items || items.length < 5) {
    if (import.meta.env.DEV) console.log('[FocusHeatingUp] ❌ Not enough items:', items?.length || 0);
    return null;
  }
  
  const recent14Days = items.filter(item => daysAgo(item.created_at) <= 14);
  const dormant = items.filter(item => {
    const days = daysAgo(item.created_at);
    return days >= 14 && days <= 90;
  });
  
  if (import.meta.env.DEV) console.log('[FocusHeatingUp] Recent (14d):', recent14Days.length, '| Dormant (14-90d):', dormant.length);
  
  if (recent14Days.length < 2 || dormant.length < 2) {
    if (import.meta.env.DEV) console.log('[FocusHeatingUp] ❌ Thresholds not met');
    return null;
  }
  
  const commonKeywords = findCommonKeywords(recent14Days, dormant, 1); // Lowered from 2 to 1
  
  if (import.meta.env.DEV) console.log('[FocusHeatingUp] Common keywords:', commonKeywords);
  
  if (commonKeywords.length === 0) {
    if (import.meta.env.DEV) console.log('[FocusHeatingUp] ❌ No common keywords found');
    return null;
  }
  
  const dormantMicroTopics = buildMicroTopicPhrase(dormant, 2);
  const recentMicroTopics = buildMicroTopicPhrase(recent14Days, 2);
  
  if (import.meta.env.DEV) console.log('[FocusHeatingUp] ✅ Detected!');
  
  return {
    type: 'focus_heating_up',
    title: 'Focus Heating Up',
    subtitle: commonKeywords[0],
    shortReason: `This ${commonKeywords[0]} topic is returning after a dormant period.`,
    reason: `You revisited ${commonKeywords[0]} after letting it sit dormant for weeks. Earlier you saved ${dormant.length} pieces touching on ${dormantMicroTopics}, then went quiet. Now you have saved ${recent14Days.length} new items in the past two weeks—${recentMicroTopics}—that share keywords and themes with the older cluster. This is a clear resurgence pattern. When dormant topics heat back up with new saves, it usually means something external triggered renewed interest, or your thinking evolved enough to return with fresh perspective. Either way, something is brewing, and this theme is worth consolidating into a Focus before it scatters again.`,
    action: 'Consider turning this into a Focus.',
    metadata: {
      dormantCount: dormant.length,
      recentCount: recent14Days.length,
      commonKeywords
    }
  };
}

/**
 * 4. GROUP THESE TOGETHER
 * Trigger: 3+ items in last 14 days that visually/semantically cluster
 */
function detectGroupTheseTogether(items) {
  if (!items || items.length < 3) {
    if (import.meta.env.DEV) console.log('[GroupTogether] ❌ Not enough items:', items?.length || 0);
    return null;
  }
  
  const recent14Days = items.filter(item => daysAgo(item.created_at) <= 14);
  
  if (import.meta.env.DEV) console.log('[GroupTogether] Recent items (14d):', recent14Days.length);
  
  if (recent14Days.length < 3) {
    if (import.meta.env.DEV) console.log('[GroupTogether] ❌ Need at least 3 recent items');
    return null;
  }
  
  // Check if items share common micro-topics
  const allTopics = extractMicroTopics(recent14Days);
  const wordCounts = {};
  
  allTopics.forEach(topic => {
    const words = topic.split(/\s+/).filter(w => w.length > 2); // Lowered from 3 to 2 chars
    words.forEach(word => {
      wordCounts[word] = (wordCounts[word] || 0) + 1;
    });
  });
  
  if (import.meta.env.DEV) console.log('[GroupTogether] Word counts:', Object.keys(wordCounts).length, 'unique words');
  
  const strongKeywords = Object.entries(wordCounts)
    .filter(([word, count]) => count >= 2) // Lowered threshold
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([word]) => word);
  
  if (import.meta.env.DEV) console.log('[GroupTogether] Strong keywords:', strongKeywords);
  
  // Even if no strong keywords, still show the card if we have recent items
  const microTopics = buildMicroTopicPhrase(recent14Days, 3);
  const itemCount = recent14Days.length;
  
  if (import.meta.env.DEV) console.log('[GroupTogether] ✅ Detected! Items:', itemCount);
  
  return {
    type: 'group_together',
    title: 'Group These Together',
    subtitle: microTopics,
    shortReason: `These ${itemCount} recent items are clustering together.`,
    reason: `You saved ${itemCount} pieces in the past two weeks—${microTopics}—and they are clustering around shared keywords: ${strongKeywords.slice(0, 3).join(', ')}. You did not explicitly organize these, but they are forming a semantic group on their own. This is how moodboards and plans begin: unconscious collecting around a vibe, direction, or project before you have language for what you are building. When items cluster like this without planning, it usually means your intuition is ahead of your intention. These pieces are ready to be grouped into a Focus or turned into an actionable plan before the pattern fades.`,
    action: 'Group these into a Focus or Plan.',
    metadata: {
      itemCount,
      keywords: strongKeywords,
      items: recent14Days.map(item => item.id)
    }
  };
}

/**
 * Main detection function - returns all valid memory surfacing cards
 * NOW USES UNIFIED VAULT SERVICE for all content sources
 */
export async function detectMemorySurfacingCards(userId) {
  try {
    // Get all items from unified vault (last 90 days)
    const vaultItems = await vaultService.getAllItems(userId, { 
      daysBack: 90,
      forceRefresh: false 
    });
    
    if (import.meta.env.DEV) console.log('[MemorySurfacing] Loaded', vaultItems.length, 'items from unified vault');
    
    // Map vault items to the format expected by detectors (snake_case)
    const allItems = vaultItems.map(item => ({
      id: item.id,
      user_id: item.userId,
      title: item.title,
      description: item.description,
      url: item.url,
      created_at: item.createdAt,
      source_type: item.sourceType,
      auto_tags: item.tags || item.autoTags?.topics || [],
      _source: item.originalSourceTable
    }));
    
    if (allItems.length < 5) {
      if (import.meta.env.DEV) console.log('[MemorySurfacing] Not enough items (need 5+, have', allItems.length, ')');
      return [];
    }
    
    // Run all detectors
    const cards = [
      detectForgottenGem(allItems),
      detectLastMonthHighlights(allItems),
      detectFocusHeatingUp(allItems),
      detectGroupTheseTogether(allItems)
    ].filter(card => card !== null);
    
    if (import.meta.env.DEV) console.log('[MemorySurfacing] Detected cards:', cards.length);
    
    return cards;
    
  } catch (error) {
    console.error('[MemorySurfacing] Error:', error);
    return [];
  }
}

/**
 * Get cached or fresh memory surfacing cards
 * Caches for 24 hours
 */
export async function getMemorySurfacingCards(userId, forceRefresh = false) {
  const cacheKey = `memory_surfacing_v2_${userId}`; // v2: Fixed capitalization
  const cacheTimestampKey = `${cacheKey}_timestamp`;
  
  if (!forceRefresh) {
    const cached = localStorage.getItem(cacheKey);
    const timestamp = localStorage.getItem(cacheTimestampKey);
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      if (age < maxAge) {
        const parsedCache = JSON.parse(cached);
        if (import.meta.env.DEV) console.log('[MemorySurfacing] Using cached cards:', parsedCache.length);
        return parsedCache;
      }
    }
  }
  
  if (import.meta.env.DEV) console.log('[MemorySurfacing] Generating fresh cards...');
  
  // Generate fresh cards
  const cards = await detectMemorySurfacingCards(userId);
  
  // Cache results
  localStorage.setItem(cacheKey, JSON.stringify(cards));
  localStorage.setItem(cacheTimestampKey, Date.now().toString());
  
  if (import.meta.env.DEV) console.log('[MemorySurfacing] Cached', cards.length, 'cards');
  
  return cards;
}

/**
 * Clear memory surfacing cache for a user
 * Useful for debugging or forcing refresh
 */
export function clearMemorySurfacingCache(userId) {
  const cacheKey = `memory_surfacing_v2_${userId}`; // v2: Fixed capitalization
  const cacheTimestampKey = `${cacheKey}_timestamp`;
  localStorage.removeItem(cacheKey);
  localStorage.removeItem(cacheTimestampKey);
  if (import.meta.env.DEV) console.log('[MemorySurfacing] Cache cleared for user:', userId);
}

